import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import React from 'react';
import { Link } from '../utils/router/v3Bridge';
import AvatarWithAction from '../components/AvatarWithAction';
import Timestamp from '../components/Timestamp';
import Tooltip from '../components/Tooltip';
import {
  NotificationReadStatus,
  NotificationStatus,
} from '../graphql/generated/graphql';
import PostDetailPreviewPopOver from '../post/PostDetailPreviewPopOver';
import notificationPath from '../utils/url/notificationPath';
import getNotificationPostId from './getNotificationPostId';
import styles from './NotificationItem.module.scss';
import NotificationMessage from './NotificationMessage';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const SET_NOTIFICATIONS_STATUS_MUTATION = gql(`
  mutation setNotificationsStatus(
    $ids: [ID]!
    $status: NotificationReadStatus!
  ) {
    markNotifications(ids: $ids, status: $status) {
      id
      status
    }
  }
`);

const NOTIFICATIONS_FRAGMENT = gql(`
  fragment NotificationItem_notifications on Notification {
    id
    status
    type
    insertedAt
    actor {
      ...AvatarWithAction_user
    }
    ... on ReactionNotification {
      reaction {
        emoji
      }
    }
    ...getNotificationPostId_notification
    ...notificationPath_notifications
    ...NotificationMessage_notifications
  }
`);

interface NotificationItemProps {
  notifications: DocumentType<typeof NOTIFICATIONS_FRAGMENT>[];
  onNavigate?: () => void;
}

const NotificationItem = ({
  notifications,
  onNavigate,
}: NotificationItemProps) => {
  const notification = notifications[0];

  const [setStatus] = useMutation(SET_NOTIFICATIONS_STATUS_MUTATION);

  if (!notification) {
    return null;
  }

  const notificationPostId = getNotificationPostId(notification);

  const status = notifications.every(
    (n) => n.status === NotificationStatus.Read,
  )
    ? NotificationReadStatus.Read
    : NotificationReadStatus.Unread;

  const toggleStatus = () =>
    setStatus({
      variables: {
        ids: notifications.map(({ id }) => id),
        status:
          status === NotificationReadStatus.Read
            ? NotificationReadStatus.Unread
            : NotificationReadStatus.Read,
      },
      optimisticResponse: {
        __typename: 'RootMutationType',
        markNotifications: notifications.map(({ id, __typename }) => ({
          __typename,
          id,
          status:
            status === NotificationReadStatus.Read
              ? NotificationStatus.Sent
              : NotificationStatus.Read,
        })),
      },
    });

  const markButtonLabel = `Mark as ${
    status === NotificationReadStatus.Unread ? 'read' : 'unread'
  }`;

  const content = (
    <Link
      to={notificationPath(notifications)}
      className={styles.notificationItem}
      onClick={(e) => {
        if (status === NotificationReadStatus.Unread) {
          toggleStatus();
        }
        if (onNavigate && !e.metaKey && !e.ctrlKey) {
          onNavigate();
        }
      }}
    >
      <div className={styles.avatarContainer}>
        {notification.actor && (
          <AvatarWithAction
            user={notification.actor}
            action={notification.type}
            emoji={
              'reaction' in notification
                ? notification.reaction?.emoji
                : undefined
            }
          />
        )}
      </div>
      <div className={styles.mainContent}>
        <div className={styles.text}>
          <NotificationMessage notifications={notifications} />
        </div>
        <div className={styles.timestamp}>
          <Timestamp date={notification.insertedAt} />
        </div>
      </div>
      <div className={styles.readControl}>
        <div className={styles.dotContainer}>
          <Tooltip
            popOverPortal={document.body}
            tooltip={markButtonLabel}
            placement="top"
            trigger="hover"
          >
            <button
              type="button"
              className={classNames(styles.dot, {
                [styles.unread]: status === NotificationReadStatus.Unread,
              })}
              onClick={(e) => {
                toggleStatus();
                e.preventDefault();
                e.stopPropagation();
              }}
              aria-label={markButtonLabel}
            />
          </Tooltip>
        </div>
      </div>
    </Link>
  );

  if (notificationPostId) {
    return (
      <div className={styles.container}>
        <PostDetailPreviewPopOver postId={notificationPostId}>
          {content}
        </PostDetailPreviewPopOver>
      </div>
    );
  }

  return <div className={styles.container}>{content}</div>;
};

export default NotificationItem;
